import { Component, Fragment, Host, Prop, State, h } from '@stencil/core';

import imageActions from '../../../actions/actions.image-capture';
import { ImageCaptureEvents, trackImageCapture } from '../../../tracking/tracking.imageCapture';
import { ImageCategory, UploadStatus, toIconName } from '../../../utils/utils.image-capture';

@Component({
  tag: 'image-capture-photobox',
  styleUrl: 'image-capture-photobox.css',
})
export class ImageCapturePhotobox {
  @Prop() imageGroupGuid = '';
  @Prop() category: ImageCategory = ImageCategory.DriversSide;
  @Prop() imageUrl: string = '';

  @State() uploadStatus = UploadStatus.Empty;
  @State() backgroundImage: string = '';

  private imageInput: HTMLInputElement;
  private deletePopup: HTMLImageCaptureDeletePopupElement;

  componentWillLoad() {
    if (this.imageUrl) {
      this.backgroundImage = `url(${this.imageUrl})`;
      this.uploadStatus = UploadStatus.Success;
    }
  }

  isEmpty() {
    return this.uploadStatus === UploadStatus.Empty;
  }

  isUploading() {
    return this.uploadStatus === UploadStatus.Loading;
  }

  isSuccess() {
    return this.uploadStatus === UploadStatus.Success;
  }

  isError() {
    return this.uploadStatus === UploadStatus.Error;
  }

  renderTitle(marginClass = '', visible = true) {
    const classes = {
      'whitespace-nowrap text-center text-xs': true,
      [marginClass]: true,
      invisible: !visible,
    };
    return <div class={classes}>{this.category}</div>;
  }

  renderEmptyState() {
    const iconName = toIconName(this.category);
    return (
      <div class="flex justify-between h-full max-h-full flex-col">
        {this.renderTitle('py-2 shrink-0')}
        <svg-icon icon={`image-capture-${iconName}`} class="flex mx-auto w-3/5 h-2/5 shrink-0" />
        <div class="py-3 text-center text-xs shrink-0">
          {this.isEmpty() && (
            <ico-hyperlink noUnderline onClick={(evt) => this.importImage(evt)} data-testid="imageCaptureAddPhoto">
              Add Photo
            </ico-hyperlink>
          )}
          {this.isUploading() && <span>Uploading</span>}
        </div>
      </div>
    );
  }

  importImage(evt) {
    evt.preventDefault();
    trackImageCapture(ImageCaptureEvents.AddPhoto, 'click');
    this.imageInput.click();
  }

  renderSuccessState() {
    const iconClasses = {
      'h-6 w-6 from-black/50 via-black/0 to-black/0 p-2 pb-6': true,
    };
    const deleteClasses = {
      ...iconClasses,
      'bg-gradient-to-br pr-6': true,
    };
    const checkmarkClasses = {
      ...iconClasses,
      'bg-gradient-to-bl pl-6': true,
    };
    return (
      <Fragment>
        <image-capture-delete-popup ref={(el) => (this.deletePopup = el)} whenClickedDelete={() => this.removeImage()} />
        <div class="flex h-full justify-between overflow-hidden rounded-2xl bg-cover bg-center" style={{ 'background-image': this.backgroundImage }}>
          <div class={deleteClasses}>
            <svg-icon icon="image-capture-trash" height="1.5rem" class="cursor-pointer" data-testid="imageCaptureDeleteButton" onClick={() => this.confirmDeleteImage()} />
          </div>
          <div class={checkmarkClasses}>
            <svg-icon icon="image-capture-checkmark" height="1.5rem" />
          </div>
        </div>
      </Fragment>
    );
  }

  confirmDeleteImage() {
    this.deletePopup.open();
  }

  async removeImage() {
    if (await imageActions.onDelete(this.imageGroupGuid, this.category)) {
      this.imageInput.value = '';
      this.backgroundImage = '';
      this.uploadStatus = UploadStatus.Empty;
    }
  }

  renderErrorState() {
    return (
      <div class="text-center text-sm" data-testid="imageCapturePhotoboxUploadFailed">
        <svg-icon icon="image-capture-alert" height="1.5rem" />

        <div class="mt-1 mb-2">
          {this.category}
          <br />
          Failed to Upload
        </div>
        <ico-hyperlink noUnderline onClick={(evt) => this.importImage(evt)} data-testid="imageCaptureTryAgain">
          Try Again
        </ico-hyperlink>
      </div>
    );
  }

  renderInput() {
    return (
      <input
        type="file"
        class="hidden"
        name="photo"
        accept="image/*"
        title={this.category}
        onChange={(evt) => this.onImageSelected(evt)}
        ref={(el) => {
          this.imageInput = el;
        }}
      />
    );
  }

  onImageSelected(event) {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = async () => {
      this.uploadStatus = UploadStatus.Loading;
      const success = await imageActions.onUpload(file, this.imageGroupGuid, this.category);
      this.uploadStatus = success ? UploadStatus.Success : UploadStatus.Error;
      if (success) {
        this.backgroundImage = `url(${URL.createObjectURL(file)})`;
      } else {
        this.imageInput.value = '';
      }
    };
    reader.readAsDataURL(file);
  }

  render() {
    const classes = {
      'flex flex-col justify-center aspect-square w-auto': true,
      'box-border rounded-2xl border border-solid border-clrs-neutral-true-grey': true,
      'border-clrs-argo-red bg-red-50 text-clrs-argo-red': this.isError(),
      'select-none': true,
    };
    return (
      <Host>
        {this.renderTitle('my-1', this.isSuccess())}
        <div class={classes}>
          {(this.isEmpty() || this.isUploading()) && this.renderEmptyState()}
          {this.isSuccess() && this.renderSuccessState()}
          {this.isError() && this.renderErrorState()}
          {this.renderInput()}
        </div>
      </Host>
    );
  }
}
